import React, { useState } from 'react';
import NextLink from 'next/link';
import { SVGIcon, Typography } from '@loomstateorg/shared-frontend';
import Image from 'frontastic/lib/image';

export interface MiniCard {
  text: string;
  icon?: {
    [key: string]: any;
  };
  href: string;
  backgroundColor?: string;
  backgroundImage?: {
    [key: string]: any;
  };
  categoryColor?: string;
  backgroundHoverColor?: string;
  categoryFontSize?: '1x' | '1.25x' | '1.5x' | '1.75x' | '2x';
  contentVerticalAlign?: 'top' | 'center' | 'bottom';
  contentHorizontalAlign?: 'left' | 'center' | 'right';
  contentDirection?: 'row' | 'row-reverse';
}

export interface Props {
  miniCard: MiniCard;
  index?: number;
  children?: React.ReactNode;
}

const MiniCard: React.FC<Props> = ({ miniCard, index = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);

  const containerStyle = `relative overflow-hidden min-h-[120px] flex items-center justify-center px-20 bg-100 hover:shadow-lg transition-all duration-300 w-full cursor-pointer`;

  const icon = miniCard.icon ? <SVGIcon src={miniCard.icon.fields.file.url} alt={miniCard.icon.fields.title} /> : null;

  const getVerticalAlignClass = () => {
    switch (miniCard.contentVerticalAlign) {
      case 'top':
        return 'justify-start';
      case 'bottom':
        return 'justify-end';
      case 'center':
      default:
        return 'justify-center';
    }
  };

  const getTextAlignClass = () => {
    switch (miniCard.contentHorizontalAlign) {
      case 'center':
        return 'text-center mx-auto';
      case 'right':
        return `text-right ml-auto ${miniCard.contentDirection === 'row' ? 'mr-10' : ''}`;
      case 'left':
        return `text-left mr-auto ${miniCard.contentDirection === 'row-reverse' ? 'ml-10' : ''}`;
      default:
        return 'text-left mr-auto';
    }
  };

  const getIconSizePx = () => {
    const sizeMap: Record<string, number> = {
      '1x': 24,
      '1.25x': 28,
      '1.5x': 32,
      '1.75x': 36,
      '2x': 40,
    };
    return sizeMap[miniCard.categoryFontSize || '1x'] || 24;
  };

  const getFontSize = (defaultSize: number, size: '1x' | '1.25x' | '1.5x' | '1.75x' | '2x') => {
    const fontSizes = {
      '1x': 1,
      '1.25x': 1.25,
      '1.5x': 1.5,
      '1.75x': 1.75,
      '2x': 2,
    };
    const fontSize = Math.round(defaultSize * fontSizes[size]);

    return fontSize;
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const dynamicStyle = {
    backgroundColor:
      isHovered && miniCard.backgroundHoverColor ? miniCard.backgroundHoverColor : miniCard.backgroundColor,
    color: miniCard.categoryColor,
  };

  return (
    <div className="size-full bg-gray-100" data-testid="quick-action-card" data-component-group="cta-card">
      <NextLink href={miniCard.href} data-testid={`quick-action-${index}`}>
        <div className="block size-full">
          <div
            data-component-group="cta-card"
            className={containerStyle + ' h-full'}
            style={dynamicStyle}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {miniCard.backgroundImage?.fields?.file?.url && (
              <div className="pointer-events-none absolute inset-0 z-0">
                <Image
                  src={miniCard.backgroundImage.fields.file.url}
                  fill={true}
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  priority={true}
                  alt={miniCard.text}
                />
              </div>
            )}
            <div className={`relative z-10 flex size-full flex-col ${getVerticalAlignClass()}`}>
              <div
                className={`flex w-full items-center justify-between ${
                  miniCard.contentDirection === 'row-reverse' ? 'flex-row-reverse' : ''
                }`}
              >
                <Typography
                  className={`text-24 md:text-${getFontSize(16, miniCard.categoryFontSize || '1x')} ${getTextAlignClass()}`}
                  style={{
                    lineHeight: '1.2',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: '60px',
                  }}
                  data-testid={`quick-action-title-${index}`}
                >
                  {miniCard.text}
                </Typography>
                {icon && (
                  <div className="shrink-0" style={{ width: getIconSizePx(), height: getIconSizePx() }}>
                    {icon}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </NextLink>
    </div>
  );
};

export default MiniCard;
